/**
 * Native Push Notifications Service
 * Handles registration and receiving of push notifications
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { isNativePlatform } from '../utils/capacitor';
import { csrfHeaders } from '../utils/csrf';

let pushToken = null;
let notificationListeners = [];

/**
 * Initialize push notifications
 * @returns {Promise<string>} - Push token
 */
export const initializePushNotifications = async () => {
  if (!isNativePlatform()) {
    console.log('Push notifications only available on native platforms');
    return null;
  }

  try {
    // Request permission
    const permissionStatus = await PushNotifications.requestPermissions();
    
    if (permissionStatus.receive !== 'granted') {
      console.warn('Push notification permission not granted');
      return null;
    }

    // Register with operating system
    await PushNotifications.register();

    // Listen for registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration success, token:', token.value);
      pushToken = token.value;
      
      // Send token to backend for storage
      sendTokenToBackend(token.value);
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error);
    });

    // Listen for push notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      
      // Notify all registered listeners
      notificationListeners.forEach(listener => {
        try {
          listener(notification);
        } catch (error) {
          console.error('Error in notification listener:', error);
        }
      });
    });

    // Listen for notification actions
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      
      // Handle notification tap
      if (action.actionId === 'tap') {
        handleNotificationTap(action.notification);
      }
    });

    return pushToken;
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    throw error;
  }
};

/**
 * Send push token to backend
 * @param {string} token - FCM/APNS token
 */
const sendTokenToBackend = async (token) => {
  try {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const response = await fetch(`${API}/user/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({ token })
    });

    if (!response.ok) {
      console.error('Failed to send push token to backend');
    }
  } catch (error) {
    console.error('Error sending push token:', error);
  }
};

/**
 * Handle notification tap
 * @param {Object} notification - Notification data
 */
const handleNotificationTap = (notification) => {
  // Navigate based on notification data
  const { data } = notification;
  
  if (data?.route) {
    // Use React Router to navigate
    window.location.href = data.route;
  } else if (data?.deadline_id) {
    window.location.href = '/reminders';
  } else if (data?.transaction_id) {
    window.location.href = '/transactions';
  }
};

/**
 * Get current push token
 * @returns {string|null}
 */
export const getPushToken = () => {
  return pushToken;
};

/**
 * Add notification listener
 * @param {Function} callback - Called when notification received
 */
export const addNotificationListener = (callback) => {
  notificationListeners.push(callback);
  
  // Return unsubscribe function
  return () => {
    notificationListeners = notificationListeners.filter(l => l !== callback);
  };
};

/**
 * Check if notifications are enabled
 * @returns {Promise<boolean>}
 */
export const areNotificationsEnabled = async () => {
  if (!isNativePlatform()) {
    return false;
  }

  try {
    const permissions = await PushNotifications.checkPermissions();
    return permissions.receive === 'granted';
  } catch (error) {
    console.error('Error checking notification permissions:', error);
    return false;
  }
};

/**
 * Request notification permissions
 * @returns {Promise<boolean>}
 */
export const requestNotificationPermissions = async () => {
  if (!isNativePlatform()) {
    return false;
  }

  try {
    const permissions = await PushNotifications.requestPermissions();
    return permissions.receive === 'granted';
  } catch (error) {
    console.error('Error requesting notification permissions:', error);
    return false;
  }
};

/**
 * Get delivered notifications (iOS only)
 * @returns {Promise<Array>}
 */
export const getDeliveredNotifications = async () => {
  if (!isNativePlatform()) {
    return [];
  }

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (error) {
    console.error('Error getting delivered notifications:', error);
    return [];
  }
};

/**
 * Remove delivered notifications
 * @param {Array<Object>} notifications - Notifications to remove
 */
export const removeDeliveredNotifications = async (notifications) => {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await PushNotifications.removeDeliveredNotifications({ notifications });
  } catch (error) {
    console.error('Error removing notifications:', error);
  }
};

/**
 * Remove all delivered notifications
 */
export const removeAllDeliveredNotifications = async () => {
  if (!isNativePlatform()) {
    return;
  }

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('Error removing all notifications:', error);
  }
};
